import React, { useCallback, useEffect, useMemo, useState } from "react";
import OuterDraggable from "react-draggable";
import { CopyToClipboard } from "react-copy-to-clipboard";

import "../../smartCharts/smartCharts.css";
import { useDispatch, useSelector } from "react-redux";
import actions from "../actions";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import Loader from "../../common/components/Loader";
import { EllipsisTooltipText } from "../../common/components/EllipsisTextWithTooltip";
import { showAlert } from "../../../config/utils";

export const SmartImportFieldsConfig = ({ onSave, onCancel }) => {
    const [search, setSearch] = useState("");
    const [state, setState] = useState({});
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [copiedLabel, setCopiedLabel] = useState(null);
    const dispatch = useDispatch();
    const { id } = useParams();
    const { smartImportFields } = useSelector(state => state.assetReducer);
    const [saveLoader, setSaveLoader] = useState(false);

    useEffect(() => {
        if (!copiedLabel) return;
        const timer = setTimeout(() => setCopiedLabel(null), 2000);
        return () => clearTimeout(timer);
    }, [copiedLabel]);

    useEffect(() => {
        dispatch(actions.smartImportFields({ client_id: id, config_type: "asset_import_field_config" }));
    }, [dispatch, id]);

    useEffect(() => {
        if (smartImportFields?.success && smartImportFields.data?.fields) {
            setState(smartImportFields.data.fields);
        }
    }, [smartImportFields]);

    const filteredFields = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return state;
        return Object.fromEntries(Object.entries(state).filter(([label]) => label.toLowerCase().includes(q)));
    }, [search, state]);

    const allActive = useMemo(() => Object.values(filteredFields).every(f => f.active), [filteredFields]);
    const toggleAllActive = () => {
        const select = !allActive;
        setState(prev =>
            Object.fromEntries(Object.entries(prev).map(([label, field]) => [label, filteredFields[label] ? { ...field, active: select } : field]))
        );
    };

    const identifierEnabledKeys = useMemo(
        () =>
            Object.entries(filteredFields)
                .filter(([_, f]) => uniqueIdentifierFields.includes(f.key))
                .map(([label]) => label),
        [filteredFields]
    );
    const allIdentifier = useMemo(
        () => identifierEnabledKeys.length > 0 && identifierEnabledKeys.every(label => filteredFields[label].is_identifier),
        [identifierEnabledKeys, filteredFields]
    );
    const toggleAllIdentifier = () => {
        const select = !allIdentifier;
        setState(prev =>
            Object.fromEntries(
                Object.entries(prev).map(([label, field]) => [
                    label,
                    identifierEnabledKeys.includes(label) ? { ...field, is_identifier: select } : field
                ])
            )
        );
    };

    const createEnabledKeys = useMemo(
        () =>
            Object.entries(filteredFields)
                .filter(([_, f]) => canCreateFields.includes(f.key))
                .map(([label]) => label),
        [filteredFields]
    );
    const allCreate = useMemo(
        () => createEnabledKeys.length > 0 && createEnabledKeys.every(label => filteredFields[label].can_create),
        [createEnabledKeys, filteredFields]
    );
    const toggleAllCreate = () => {
        const select = !allCreate;
        setState(prev =>
            Object.fromEntries(
                Object.entries(prev).map(([label, field]) => [label, createEnabledKeys.includes(label) ? { ...field, can_create: select } : field])
            )
        );
    };

    const toggleField = (label, key) => {
        setState(prev => ({
            ...prev,
            [label]: { ...prev[label], [key]: !prev[label][key] }
        }));
    };

    const handleSave = () => {
        setSaveLoader(true);
        const result = dispatch(
            actions.saveSmartImportFields({
                client_id: id,
                config_type: "asset_import_field_config",
                config: { fields: state }
            })
        );

        if (result && typeof result.then === "function") {
            result
                .then(() => {
                    setSaveLoader(false);
                    showAlert("Saved Successfully");
                    onSave();
                })
                .catch(() => {
                    setSaveLoader(false);
                    showAlert("Oops. Failed to save import column config!");
                });
        } else {
            setSaveLoader(false);
        }
    };

    const handleTransform = useCallback((e, ui) => setTransform({ left: ui.x, top: ui.y }), []);
    const handleClearSearch = () => setSearch("");

    return (
        <div id="smart-import-config" className="modal modal-region modal-view reco-view-mdl column-setting-modal" style={{ display: "block" }}>
            <div className="modal-dialog" role="document">
                <OuterDraggable handle=".draggable" cancel=".not-draggable" onDrag={handleTransform}>
                    <div className="modal-content outerModal_draggable" style={transform}>
                        <div className="modal-header draggable d-flex justify-content-between align-items-center">
                            <h5 className="modal-title">Import Settings</h5>
                            <button type="button" className="close not-draggable" onClick={onCancel}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="" />
                                </span>
                            </button>
                        </div>
                        <div className="modal-body region-otr">
                            <div className="global-controls mb-3">
                                <div className="d-flex align-items-end mb-2">
                                    <div className="flex-grow-1 position-relative">
                                        <i
                                            className="fas fa-search position-absolute"
                                            style={{ left: "10px", top: "50%", transform: "translateY(-50%)", color: "#aaa" }}
                                        />
                                        <input
                                            type="text"
                                            className="form-control pl-5"
                                            placeholder="Search Columns"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                        {search && (
                                            <i
                                                className="fas fa-times position-absolute"
                                                style={{ right: "10px", top: "50%", transform: "translateY(-50%)", color: "#aaa", cursor: "pointer" }}
                                                onClick={handleClearSearch}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            {smartImportFields?.loading ? (
                                <div className="col-md-12 p-5 box-otr box-wide" style={{ height: 450 }}>
                                    <Loader />
                                </div>
                            ) : (
                                <div className="bbi-table-config-container smart-import-settings">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 100 }}>
                                                    <label className="container-check mb-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={allActive}
                                                            onChange={toggleAllActive}
                                                            className="custom-checkbox"
                                                        />
                                                        <span className="checkmark" />
                                                        Active
                                                    </label>
                                                </th>
                                                <th>Label</th>
                                                <th style={{ width: 130 }}>Field Type</th>
                                                <th style={{ width: 200 }}>
                                                    <label className="container-check mb-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={allIdentifier}
                                                            onChange={toggleAllIdentifier}
                                                            className="custom-checkbox"
                                                        />{" "}
                                                        <span className="checkmark" />
                                                        Is Unique Identifier
                                                    </label>
                                                </th>
                                                <th style={{ width: 200 }}>
                                                    <label className="container-check mb-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={allCreate}
                                                            onChange={toggleAllCreate}
                                                            className="custom-checkbox"
                                                        />{" "}
                                                        <span className="checkmark" />
                                                        Creation Accepted
                                                    </label>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredFields && Object.entries(filteredFields)?.length ? (
                                                Object.entries(filteredFields).map(([label, field]) => (
                                                    <tr key={label}>
                                                        <td style={{ width: 100 }}>
                                                            <label className="container-check mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={field.active}
                                                                    onChange={() => toggleField(label, "active")}
                                                                    className="custom-checkbox"
                                                                />
                                                                <span className="checkmark" />
                                                            </label>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center draggable-item">
                                                                <EllipsisTooltipText text={label} className="text-line-clamp-2" />
                                                                <CopyToClipboard text={label} onCopy={() => setCopiedLabel(label)}>
                                                                    <i
                                                                        className="far fa-copy cursor-pointer ml-2 text-muted copy-icon"
                                                                        title="Copy label"
                                                                    />
                                                                </CopyToClipboard>
                                                                {copiedLabel === label && <span className="ml-2 text-success">Copied!</span>}
                                                            </div>
                                                        </td>
                                                        <td style={{ width: 130 }}>
                                                            <div className="d-flex align-items-center draggable-item">
                                                                <EllipsisTooltipText
                                                                    text={field.is_dynamic_field ? "Dynamic" : "Static"}
                                                                    className="text-line-clamp-2"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td style={{ width: 200 }}>
                                                            <label
                                                                className={`container-check mb-0 ${
                                                                    !uniqueIdentifierFields.includes(field.key) ? "cursor-disabled" : ""
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={field.is_identifier}
                                                                    disabled={!uniqueIdentifierFields.includes(field.key)}
                                                                    className={`custom-checkbox ${
                                                                        !uniqueIdentifierFields.includes(field.key) ? "cursor-disabled" : ""
                                                                    }`}
                                                                    onChange={() => toggleField(label, "is_identifier")}
                                                                />
                                                                <span className="checkmark" />
                                                            </label>
                                                        </td>
                                                        <td style={{ width: 200 }}>
                                                            <label
                                                                className={`container-check mb-0 ${
                                                                    !canCreateFields.includes(field.key) ? "cursor-disabled" : ""
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={field.can_create}
                                                                    disabled={!canCreateFields.includes(field.key)}
                                                                    className={`custom-checkbox ${
                                                                        !canCreateFields.includes(field.key) ? "cursor-disabled" : ""
                                                                    }`}
                                                                    onChange={() => toggleField(label, "can_create")}
                                                                />
                                                                <span className="checkmark" />
                                                            </label>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5}>
                                                        <label>No Columns Found</label>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onCancel}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleSave}>
                                {saveLoader ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </OuterDraggable>
            </div>
        </div>
    );
};

const uniqueIdentifierFields = [
    "assets.asset_type_id",
    "assets.asset_status_id",
    "assets.region_id",
    "assets.site_id",
    "assets.building_id",
    "assets.floor_id",
    "assets.addition_id",
    "assets.uniformat_level_1_id",
    "assets.uniformat_level_2_id",
    "assets.uniformat_level_3_id",
    "assets.uniformat_level_4_id",
    "assets.uniformat_level_5_id",
    "assets.asset_condition_id",
    "assets.main_category_id",
    "assets.sub_category_1_id",
    "assets.sub_category_2_id",
    "assets.sub_category_3_id",
    "assets.client_asset_condition_id",
    "assets.trade_id",
    "assets.sub_system_id",
    "assets.system_id",
    "assets.uniformat_level_6_id",
    "assets.manufacturer_id",
    "assets.client_standards_id",
    "assets.guid",
    "assets.asset_barcode",
    "assets.asset_cmms_id",
    "assets.serial_number",
    "assets.code",
    "assets.core_model_number",
    "assets.model_number",
    "assets.rtls_tag",
    "assets.upstream_asset_barcode_number",
    "assets.linked_asset_barcode_number",
    "assets.source_panel_barcode_number",
    "assets.parent_global_id",
    "assets.asset_tag",
    "assets.asset_name",
    "assets.asset_condition",
    "assets.manufacturer",
    "assets.room_number",
    "assets.architectural_room_number",
    "assets.room_name"
];

const canCreateFields = ["assets.manufacturer"];
